import React, { useEffect, useRef, useState } from "react"
import BLUR_MAP from "../../data/hotelBlurMap.json"

// Scroll-telling épinglé façon AirPods : l'image reste fixée à l'écran pendant
// que les panneaux défilent — fondu croisé des images, légende qui se remplace,
// compteur 01/0N et points de progression. rAF passif, SSR-safe.
// items : [{ img, name, chip, desc, alt, pos?, imgMobile? }]
//   pos       : object-position de l'image (ex. "68% 50%") pour cadrer le sujet
//               quand l'écran portrait ne montre qu'un tiers de la photo
//   imgMobile : autre source sous 734 px (photo verticale), via <picture>
// Sans ces clés, rendu strictement identique (centre, même source).
const EASE = "cubic-bezier(0.22,1,0.36,1)"

// vhPerItem : hauteur de défilement allouée à chaque panneau. 115 est la
// valeur d'origine, conservée par défaut pour ne rien changer aux pages
// existantes ; on l'abaisse quand la série compte plus de 4 panneaux, sinon
// la section mange sept écrans à elle seule.
// mobileBottomInset : hauteur (px) d'une barre fixe en bas d'écran sous 820 px
// (CTA collant des pages) que la légende doit dégager ; 0 par défaut = inchangé.
const StickyStory = ({ items, eyebrow, counterAria = "", vhPerItem = 115, mobileBottomInset = 0 }) => {
  const wrapRef = useRef(null)
  const [active, setActive] = useState(0)

  useEffect(() => {
    if (typeof window === "undefined" || !wrapRef.current) return undefined
    const wrap = wrapRef.current
    // Préchargement : dès que la séquence approche (un écran d'avance), toutes
    // les images passent en eager — jamais de placeholder flouté en plein récit.
    const pre = new IntersectionObserver(
      entries => {
        if (!entries.some(e => e.isIntersecting)) return
        wrap.querySelectorAll("img").forEach(im => { im.loading = "eager" })
        pre.disconnect()
      },
      { rootMargin: "120% 0px" }
    )
    pre.observe(wrap)
    let raf = 0
    const update = () => {
      raf = 0
      const vh = window.innerHeight || 1
      const total = wrap.offsetHeight - vh
      if (total <= 0) return
      const p = Math.max(0, Math.min(0.999, (window.scrollY - wrap.offsetTop) / total))
      const idx = Math.floor(p * items.length)
      setActive(prev => (prev === idx ? prev : idx))
    }
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update) }
    window.addEventListener("scroll", onScroll, { passive: true })
    window.addEventListener("resize", onScroll, { passive: true })
    update()
    return () => {
      pre.disconnect()
      window.removeEventListener("scroll", onScroll)
      window.removeEventListener("resize", onScroll)
      if (raf) cancelAnimationFrame(raf)
    }
  }, [items.length])

  return (
    <section ref={wrapRef} aria-label={counterAria} style={{ position:"relative", height:`${items.length * vhPerItem}vh`, background:"#050505" }}>
      <div style={{ position:"sticky", top:0, height:"100svh", overflow:"hidden" }}>
        {items.map((it, i) => {
          const img = (
            <img
              key={it.img}
              src={it.img}
              alt={it.alt || it.name}
              loading={i === 0 ? "eager" : "lazy"}
              style={{
                position:"absolute", inset:0, width:"100%", height:"100%", objectFit:"cover",
                objectPosition: it.pos || undefined,
                opacity: i === active ? 1 : 0, transform: i === active ? "scale(1.04)" : "scale(1.09)",
                transition:`opacity 750ms ${EASE}, transform 1500ms ${EASE}`,
                backgroundImage: BLUR_MAP[it.img] ? `url(${BLUR_MAP[it.img]})` : undefined,
                backgroundSize:"cover", backgroundPosition: it.pos || "center",
              }}
            />
          )
          return it.imgMobile ? (
            <picture key={it.img}>
              <source media="(max-width: 733.98px)" srcSet={it.imgMobile} />
              {img}
            </picture>
          ) : img
        })}
        <div style={{ position:"absolute", inset:0, background:"linear-gradient(180deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.02) 45%, rgba(0,0,0,0.62) 100%)" }} />

        {/* Légende — remonte à chaque panneau */}
        <div key={active} className={mobileBottomInset ? "ss-legend ss-legend-inset" : "ss-legend"} style={{ position:"absolute", left:0, right:0, bottom:0, padding:"clamp(28px,5vw,72px)", animation:`ssIn 650ms ${EASE}`, "--ss-inset": `${mobileBottomInset}px` }}>
          <style>{`@keyframes ssIn { from { opacity:0; transform:translateY(22px); } to { opacity:1; transform:translateY(0); } }
            @media (max-width:820px){ .ss-legend-inset { padding-bottom:calc(28px + var(--ss-inset) + env(safe-area-inset-bottom)) !important; } }`}</style>
          <div style={{ maxWidth:680 }}>
            {eyebrow && (
              <div style={{ fontSize:12, letterSpacing:"0.24em", textTransform:"uppercase", color:"rgba(255,255,255,0.7)", marginBottom:12 }}>
                {eyebrow} · {String(active + 1).padStart(2, "0")}/{String(items.length).padStart(2, "0")}
              </div>
            )}
            <div style={{ fontSize:"clamp(30px,4.6vw,60px)", fontWeight:600, letterSpacing:"-0.045em", lineHeight:1.05, color:"#fff", textShadow:"0 2px 24px rgba(0,0,0,0.5)" }}>{items[active].name}</div>
            {items[active].chip && (
              <div style={{ display:"inline-block", marginTop:14, padding:"5px 14px", borderRadius:999, background:"rgba(255,255,255,0.14)", backdropFilter:"blur(12px)", WebkitBackdropFilter:"blur(12px)", border:"1px solid rgba(255,255,255,0.22)", color:"#fff", fontSize:13, fontWeight:600 }}>{items[active].chip}</div>
            )}
            <p style={{ fontSize:"clamp(14px,1.4vw,18px)", color:"rgba(255,255,255,0.88)", marginTop:14, lineHeight:1.55, maxWidth:560, textShadow:"0 1px 12px rgba(0,0,0,0.45)" }}>{items[active].desc}</p>
          </div>
        </div>

        {/* Points de progression */}
        <div style={{ position:"absolute", right:"clamp(16px,3vw,40px)", top:"50%", transform:"translateY(-50%)", display:"flex", flexDirection:"column", gap:10 }}>
          {items.map((_, i) => (
            <span key={i} style={{ width:6, height: i === active ? 22 : 6, borderRadius:999, background: i === active ? "#fff" : "rgba(255,255,255,0.4)", transition:`all 400ms ${EASE}` }} />
          ))}
        </div>
      </div>
    </section>
  )
}

export default StickyStory
